import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Visibility, Cancel } from "@mui/icons-material";
import { format } from "date-fns";
import { commonTableOptions } from "../../../../constants";
import { amountConverter, handleError } from "../../../../utils/common";
import { useParams } from "react-router-dom";
import { API_RESULT, API_VERSION } from "../../../../enums/common.enum";
import ErrorBoundary from "../../../../components/Helpers/ErrorBoundary";
import useAxiosInstance from "../../../../hooks/useAxiosInstance";
import { AddInvoiceModal } from "../../Modal/AddInvoiceModal";
import { CancelInvoiceModal } from "../../Modal/CancelInvoiceModal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InvoicePreviewModal from "../../Modal/InvoicePreviewModal";

const Invoices = ({
  borrowerInfo,
  setBorrowerInfo,
  visitedTabs,
  updateVisitedTabs,
}: any) => {
  const privateInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [openAddInvoice, setOpenAddInvoice] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const params = useParams();
  const activeBorrowerId = params?.borrowerId;
  const PENDING_STATUSES = ["under_review"];
  const PAID_STATUSES = ["approved"];
  const REJECTED_STATUSES = ["rejected"];
  const CANCELLED_STATUSES = ["cancelled"];
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [invoiceUrls, setInvoiceUrls] = useState<string[]>([]);

  const fetchInvoiceData = async () => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V3}/borrowers/applications?borrower_id=${activeBorrowerId}`,
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        updateVisitedTabs(2, "add");
        setData(result.data.response || []);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(() => {
    return {
      totalInvoices: data.length,

      totalPending: data.filter((i: any) =>
        PENDING_STATUSES.includes(i.status?.toLowerCase()),
      ).length,

      totalPaid: data.filter((i: any) =>
        PAID_STATUSES.includes(i.status?.toLowerCase()),
      ).length,

      totalRejected: data.filter((i: any) =>
        REJECTED_STATUSES.includes(i.status?.toLowerCase()),
      ).length,

      totalCancelled: data.filter((i: any) =>
        CANCELLED_STATUSES.includes(i.status?.toLowerCase()),
      ).length,

      totalPendingAmount: data
        .filter((i: any) => PENDING_STATUSES.includes(i.status?.toLowerCase()))
        .reduce((a: number, b: any) => a + Number(b.payable_amount), 0),

      totalPaidAmount: data
        .filter((i: any) => PAID_STATUSES.includes(i.status?.toLowerCase()))
        .reduce((a: number, b: any) => a + Number(b.payable_amount), 0),
    };
  }, [data]);

  const cols: any = [
    {
      name: "invoice_id",
      label: "Invoice ID",
      options: {
        filter: false,
      },
    },
    {
      name: "payee_name",
      label: "Payee Name",
      options: {
        filter: false,
      },
    },
    {
      name: "invoice_date",
      label: "Date",
      options: {
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];
          return (
            <Stack>
              <Typography variant="body2">
                Invoice Date:{" "}
                <strong>
                  {row.invoice_due_date
                    ? format(new Date(row.invoice_date), "dd/MM/yyyy")
                    : "-"}
                </strong>
              </Typography>
              <Typography variant="body2">
                Due Date:{" "}
                <strong>
                  {row.invoice_due_date
                    ? format(new Date(row.invoice_due_date), "dd/MM/yyyy")
                    : "-"}
                </strong>
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: "invoice_amount",
      label: "Amount",
      options: {
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];

          return (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Invoice Amount:&nbsp;
                <strong>₹{amountConverter(row.invoice_amount)}</strong>
              </Typography>
              <Typography variant="body2">
                Payable Amount:&nbsp;
                <strong>₹{amountConverter(row.payable_amount)}</strong>
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: "invoice_added_at",
      label: "Invoice Added At",
      options: {
        filter: false,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        customBodyRender: (value: string) => {
          const colorMap: any = {
            approved: { bg: "#C6F6D5", color: "#22543D" },
            pending: { bg: "#FEEBC8", color: "#7B341E" },
            paid: { bg: "#E6FFFA", color: "#065F46" },
            cancelled: { bg: "#FED7D7", color: "#822727" },
            rejected: { bg: "#FED7D7", color: "#822727" },
          };

          return (
            <Chip
              size="small"
              label={value.toUpperCase()}
              sx={{
                backgroundColor: colorMap[value]?.bg,
                color: colorMap[value]?.color,
                fontWeight: 500,
              }}
            />
          );
        },
      },
    },
    {
      name: "actions",
      label: "View Invoice",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];

          return (
            <Stack direction="row" spacing={1} justifyContent="center">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();

                  const urls = Array.isArray(row.invoice_file_url)
                    ? row.invoice_file_url
                    : [row.invoice_file_url];

                  if (urls.length === 1) {
                    window.open(urls[0], "_blank");
                  } else {
                    setInvoiceUrls(urls);
                    setOpenInvoiceModal(true);
                  }
                }}
              >
                <Visibility fontSize="small" color="primary" />
              </IconButton>

              {/* {!["paid", "cancelled", "rejected"].includes(row.status) && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedInvoice(row);
                    setOpenCancelModal(true);
                  }}
                >
                  <Cancel fontSize="small" />
                </IconButton>
              )} */}

              {/* Pay Now */}
              {/* {row.status === "approved" && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => console.log("Pay", row.invoice_id)}
                >
                  Pay Now
                </Button>
              )} */}
            </Stack>
          );
        },
      },
    },
  ];

  const options: any = {
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    searchPlaceholder: "Invoice ID/Payee Name",
    rowsPerPageOptions: [10, 50, 100, 200],
    rowsPerPage: 10,
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    // setRowProps: (row: any, dataIndex: number) => ({
    //   className:
    //     data[dataIndex]?.status?.toLowerCase() !== "approved"
    //       ? "no-expand"
    //       : "",
    // }),
    // isRowExpandable: (dataIndex: number) =>
    //   data[dataIndex]?.status?.toLowerCase() === "approved",
    renderExpandableRow: (rowData: any, rowMeta: any) => {
      const row = data[rowMeta.dataIndex];

      const isCancelledOrRejected = ["cancelled", "rejected"].includes(
        row.status?.toLowerCase(),
      );

      return (
        <tr>
          <td colSpan={rowData.length + 1}>
            <Box p={2} bgcolor="#F8F9FC">
              {isCancelledOrRejected ? (
                <Box
                  sx={{
                    border: "1px solid #FCA5A5",
                    backgroundColor: "#FEF2F2",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#B91C1C" }}
                  >
                    {row.status === "cancelled"
                      ? row.cancelled_reason || "Invoice was cancelled"
                      : row.reject_reason || "Invoice was rejected"}
                  </Typography>
                </Box>
              ) : (
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Beneficiary Details
                    </Typography>

                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        Name:{" "}
                        <strong>
                          {row.beneficiary_details?.beneficiary_account_name ||
                            "--"}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Bank:{" "}
                        <strong>
                          {row.beneficiary_details?.beneficiary_bank_name ||
                            "--"}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Account No:{" "}
                        <strong>
                          {row.beneficiary_details?.beneficiary_account_no ||
                            "--"}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        IFSC:{" "}
                        <strong>
                          {row.beneficiary_details?.beneficiary_ifsc_code ||
                            "--"}
                        </strong>
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Payment Summary
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Status:{" "}
                        <strong>
                          {row.payment_details?.payment_status || "--"}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Payment Mode:{" "}
                        <strong>
                          {row.payment_details?.payment_mode || "--"}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Transaction UTR:{" "}
                        <strong>
                          {row.payment_details?.payment_utr || "--"}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Payment At:{" "}
                        <strong>
                          {row.paid_at
                            ? format(
                                new Date(row.payment_details?.payment_at),
                                "dd/MM/yyyy",
                              )
                            : "--"}
                        </strong>
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Repayment Details
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Principal Amount:&nbsp;
                        <strong>
                          ₹
                          {amountConverter(
                            row.repayment_breakup?.principle_amount || 0,
                          )}
                        </strong>
                      </Typography>

                      <Typography variant="body2">
                        Interest Rate:&nbsp;
                        <strong>
                          {row.repayment_breakup?.interest_rate ?? "--"}%
                        </strong>
                      </Typography>

                      <Typography variant="body2">
                        Total Payable:&nbsp;
                        <strong>
                          ₹
                          {amountConverter(
                            row.repayment_breakup?.total_payable || 0,
                          )}
                        </strong>
                      </Typography>

                      <Typography variant="body2">
                        Repayment Date:&nbsp;
                        <strong>
                          {row.emi_end_date
                            ? format(new Date(row.repayment_date), "dd/MM/yyyy")
                            : "--"}
                        </strong>
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          </td>
        </tr>
      );
    },
  };

  return (
    <Box>
      <Box
        mb={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
        }}
      >
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",

            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              transform: "translateY(-1px)",
            },
          }}
        >
          <Stack spacing={1.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: "0.04em" }}
            >
              INVOICE SUMMARY
            </Typography>

            <Stack
              direction="row"
              spacing={4}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Stack spacing={1}>
                {/* <Typography variant="caption" color="text.secondary">
                  Total Invoices
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {summary.totalInvoices}
                </Typography> */}

                <Stack direction="row" flexWrap="wrap">
                  {[
                    {
                      label: "Pending",
                      value: summary.totalPending,
                      color: "#92400E",
                      bg: "#FFFBEB",
                      border: "#FDE68A",
                    },
                    {
                      label: "Paid",
                      value: summary.totalPaid,
                      color: "#166534",
                      bg: "#ECFDF5",
                      border: "#A7F3D0",
                    },
                    {
                      label: "Rejected",
                      value: summary.totalRejected,
                      color: "#7C2D12",
                      bg: "#FEF2F2",
                      border: "#FECACA",
                    },
                    // {
                    //   label: "Cancelled",
                    //   value: summary.totalCancelled,
                    //   color: "#7F1D1D",
                    //   bg: "#FEF2F2",
                    //   border: "#FECACA",
                    // },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1.5,
                        backgroundColor: item.bg,
                        borderLeft: `4px solid ${item.border}`,
                        minWidth: 110,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.03em" }}
                      >
                        {item.label}
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ color: item.color }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>

              <Stack spacing={1}>
                {/* <Typography variant="caption" color="text.secondary">
                  Amounts
                </Typography> */}

                <Box>
                  <Stack spacing={0.75}>
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography variant="caption" color="text.secondary">
                        Pending Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: "#92400E" }}
                      >
                        ₹{summary.totalPendingAmount}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Utlized Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: "#166534" }}
                      >
                        ₹{summary.totalPaidAmount}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        {borrowerInfo?.status === "active" && (
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              height: 42,
              borderRadius: 1.5,
            }}
            onClick={() => setOpenAddInvoice(true)}
          >
            + Add Invoice
          </Button>
        )}
      </Box>

      <ErrorBoundary>
        <MUIDataTable title="" data={data} columns={cols} options={options} />
      </ErrorBoundary>

      <AddInvoiceModal
        borrowerInfo={borrowerInfo}
        setData={setData}
        open={openAddInvoice}
        onClose={() => setOpenAddInvoice(false)}
        onSubmit={(values: any) => {
          setOpenAddInvoice(false);
        }}
      />

      {openCancelModal && selectedInvoice && (
        <CancelInvoiceModal
          data={data}
          setData={setData}
          open={openCancelModal}
          invoice={selectedInvoice}
          onClose={() => {
            setOpenCancelModal(false);
            setSelectedInvoice(null);
          }}
        />
      )}

      {openInvoiceModal && (
        <InvoicePreviewModal
          open={openInvoiceModal}
          onClose={() => setOpenInvoiceModal(false)}
          invoiceUrls={invoiceUrls}
        />
      )}
    </Box>
  );
};

export default Invoices;
